import React, { useTransition } from "react";
import ToothBox, { ToothData, ToothPart, ToothColor } from "./ToothBox";

import "./Tooth.scss";
import { useTranslation } from "react-i18next";
import { useRef } from "react";
interface Props {
  teeth: ToothData[];
  currentColor: "felling" | "caries";
  onChange: (updatedTeeth: ToothData[]) => void;
}

const ToothChart: React.FC<Props> = ({ teeth, currentColor, onChange }) => {
  const { t } = useTranslation();
  const scrollIntervals = useRef<Record<string, NodeJS.Timeout | null>>({});

  const startAutoScroll = (key: string, direction: -1 | 1) => {
    const el = document.getElementById(`jaw-${key}`);
    if (!el) return;
    stopAutoScroll(key);
    scrollIntervals.current[key] = setInterval(() => {
      el.scrollBy({ left: 18 * direction, behavior: "smooth" });
    }, 50);
  };

  const stopAutoScroll = (key: string) => {
    const timer = scrollIntervals.current[key];
    if (timer) {
      clearInterval(timer);
      scrollIntervals.current[key] = null;
    }
  };
  const updateTooth = (id: number, parts: Record<ToothPart, ToothColor>) => {
    const updated = teeth.map((tooth) =>
      tooth.id === id ? { ...tooth, parts } : tooth
    );
    onChange(updated);
  };

  const toggleMissing = (id: number) => {
    const updated = teeth.map((tooth) =>
      tooth.id === id
        ? {
            ...tooth,
            missing: !tooth.missing,
            parts: {
              center: null,
              top: null,
              right: null,
              bottom: null,
              left: null,
            },
          }
        : tooth
    );
    onChange(updated);
  };

  // Pomoćne funkcije za sortiranje zuba po dentalnom rasporedu
  const upperLeft = teeth
    .filter((t) => t.id >= 1 && t.id <= 8)

    .map((t, index) => ({
      ...t,
      displayId: index + 1,
      groupNumber: 1,
    }))
    .reverse();

  const upperRight = teeth
    .filter((t) => t.id >= 9 && t.id <= 16)
    .map((t, index) => ({
      ...t,
      displayId: index + 1,
      groupNumber: 2,
    }));

  const lowerLeft = teeth
    .filter((t) => t.id >= 17 && t.id <= 24)
    .reverse()
    .map((t, index) => ({
      ...t,
      displayId: index + 1,
      groupNumber: 4,
    }))
    .reverse();

  const lowerRight = teeth
    .filter((t) => t.id >= 25 && t.id <= 32)
    .map((t, index) => ({
      ...t,
      displayId: index + 1,
      groupNumber: 3,
    }));

  const renderJaw = (
    label: string,
    data: any[],
    extraClass?: string
  ) => (
    <div className={`toothJaw ${extraClass ?? ""}`}>
      <div className="toothJaw__header">
        <span className="eyebrow">{label}</span>
        <div className="jawAccent" />
      </div>
      <div className="toothScrollWrapper">
        <button
          className="scrollBtn prev"
          type="button"
          aria-label={t("Scroll left")}
          onMouseEnter={() => startAutoScroll(label, -1)}
          onMouseLeave={() => stopAutoScroll(label)}
          onClick={() => stopAutoScroll(label)}
        >
          ‹
        </button>
        <div className="toothGrid" id={`jaw-${label}`}>
          {data.map((tooth: any) => (
            <div key={tooth.id} className="toothCell">
              <ToothBox
                toothId={tooth.id}
                data={tooth}
                currentColor={currentColor}
                onChange={updateTooth}
              />
              <button
                className={`missingToggle ${tooth.missing ? "is-missing" : ""}`}
                onClick={() => toggleMissing(tooth.id)}
                type="button"
              >
                {tooth.missing ? t("Restore") : t("Missing")}
              </button>
              <div className="toothNumber">
                {tooth.groupNumber}
                {tooth?.displayId ?? tooth.id}
              </div>
            </div>
          ))}
        </div>
        <button
          className="scrollBtn next"
          type="button"
          aria-label={t("Scroll right")}
          onMouseEnter={() => startAutoScroll(label, 1)}
          onMouseLeave={() => stopAutoScroll(label)}
          onClick={() => stopAutoScroll(label)}
        >
          ›
        </button>
      </div>
    </div>
  );

  return (
    <div className="toothChart">
      <div className="toothLegend">
        <span className="legendDot fill" />
        <span>{t("Felling")}</span>
        <span className="legendDot caries" />
        <span>{t("Caries")}</span>
        <span className="legendDot missing" />
        <span>{t("Missing")}</span>
      </div>
      <div className="jawColumn">
        {renderJaw(t("Upper jaw"), [...upperLeft, ...upperRight], "upper")}
        {renderJaw(t("Lower jaw"), [...lowerLeft, ...lowerRight], "lower")}
      </div>
    </div>
  );
};

export default ToothChart;
